import { PropsWithChildren, Suspense, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import App from '../../App';
import { IRootState } from '../../store';
import { toggleSidebar } from '../../store/themeConfigSlice';
import Footer from './Footer';
import Header from './Header';
import Setting from './Setting';
import Sidebar from './Sidebar';
import Portals from '../../components/Portals';
import SetupWizard from '../SetupWizard';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { selectSettings } from '../../store/authSlice';
import { getAppMode, isPathAllowedInBasicMode } from '../../config/appMode';
import { useUserContext } from '../../contexts/UserContext';
import { auth } from '../../firebaseConfig';
import FloatingActionButton from '../FloatingActionButton';
import QuickAddTransactionModal from '../QuickAddTransactionModal';
import { useKeyboardShortcuts, KeyboardShortcut } from '../../hooks/useKeyboardShortcuts';
import KeyboardShortcutsHelp from '../KeyboardShortcutsHelp';
import { useNavigate } from 'react-router-dom';
import MoneyCoachWidget from '../MoneyCoachWidget';
import ImpersonationBanner from '../ImpersonationBanner';

const DefaultLayout = () => {
    const themeConfig = useSelector((state: IRootState) => state.themeConfig);

    const dispatch = useDispatch();
    const settings = useSelector(selectSettings);
    const preferences = settings?.preferences;

    const hideSidebarByDefault = preferences && preferences?.hideSidebarByDefault !== undefined ? preferences.hideSidebarByDefault : false;
    const isBasicMode = getAppMode(settings) === 'basic';

    const [openSetup, setOpenSetup] = useState(false);
    const location = useLocation();
    const { user, loading: authLoading } = useUserContext();

    const [showTopButton, setShowTopButton] = useState(false);
    const [isQuickAddModalOpen, setIsQuickAddModalOpen] = useState(false);
    const [isShortcutsHelpOpen, setIsShortcutsHelpOpen] = useState(false);
    const navigate = useNavigate();

    const goToTop = () => {
        document.body.scrollTop = 0;
        document.documentElement.scrollTop = 0;
    };

    const onScrollHandler = () => {
        if (document.body.scrollTop > 50 || document.documentElement.scrollTop > 50) {
            setShowTopButton(true);
        } else {
            setShowTopButton(false);
        }
    };

    useEffect(() => {
        window.addEventListener('scroll', onScrollHandler);
        return () => {
            window.removeEventListener('onscroll', onScrollHandler);
        };
    }, []);

    useEffect(() => {
        if (location.pathname !== '/dashboard/users/subscription-plans' && settings !== null && !settings.currency) {
            setOpenSetup(true);
        }
    }, [settings?.currency, location.pathname]);

    // Keyboard shortcuts configuration
    const allShortcuts: KeyboardShortcut[] = [
        {
            key: 'n',
            ctrl: true,
            description: 'Add new transaction',
            action: () => setIsQuickAddModalOpen(true),
        },
        {
            key: 'd',
            ctrl: true,
            description: 'Navigate to Dashboard',
            action: () => navigate('/dashboard/index'),
            path: '/dashboard/index',
        },
        {
            key: 't',
            ctrl: true,
            description: 'Navigate to Transactions',
            action: () => navigate('/dashboard/transactions/variables'),
            path: '/dashboard/transactions/variables',
        },
        {
            key: 'a',
            alt: true,
            description: 'Navigate to Accounts',
            action: () => navigate('/dashboard/accounts'),
            path: '/dashboard/accounts',
        },
        {
            key: 'c',
            alt: true,
            description: 'Navigate to Calendar',
            action: () => navigate('/dashboard/calendar'),
            path: '/dashboard/calendar',
        },
        {
            key: '/',
            ctrl: true,
            description: 'Show keyboard shortcuts',
            action: () => setIsShortcutsHelpOpen(true),
        },
        {
            key: '?',
            shift: true,
            description: 'Show keyboard shortcuts help',
            action: () => setIsShortcutsHelpOpen(true),
        },
    ];

    // Basic mode hides some routes
    const shortcuts = isBasicMode
        ? allShortcuts.filter(s => !s.path || isPathAllowedInBasicMode(s.path))
        : allShortcuts;

    // Register keyboard shortcuts
    useKeyboardShortcuts(shortcuts);

    if (openSetup) {
        return <SetupWizard close={() => setOpenSetup(false)} />;
    }

    return (
        <App>
            {/* BEGIN MAIN CONTAINER */}
            <div className="relative">
                <ImpersonationBanner />

                {/* sidebar menu overlay */}
                <div className={`${(!themeConfig.sidebar && 'hidden') || ''} fixed inset-0 bg-[black]/60 z-50 lg:hidden`} onClick={() => dispatch(toggleSidebar(false))}></div>

                <div className="fixed bottom-[calc(6rem_+_env(safe-area-inset-bottom))] ltr:left-[calc(1.5rem_+_env(safe-area-inset-left))] rtl:right-[calc(1.5rem_+_env(safe-area-inset-right))] z-40">
                    {showTopButton && (
                        <button type="button" className="btn btn-outline-primary rounded-full p-2 animate-pulse bg-[#fafafa] dark:bg-[#060818] dark:hover:bg-primary" onClick={goToTop}>
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M8 7l4-4m0 0l4 4m-4-4v18" />
                            </svg>
                        </button>
                    )}
                </div>

                {/* BEGIN APP SETTING LAUNCHER */}
                {/* <Setting /> */}
                {/* END APP SETTING LAUNCHER */}
                {/* <WelcomeModal /> */}

                <div className={`${themeConfig.navbar} main-container text-black dark:text-white-dark min-h-screen`}>
                    {/* BEGIN SIDEBAR */}
                    <Sidebar />
                    {/* END SIDEBAR */}

                    <div className="main-content flex flex-col min-h-screen">
                        {/* BEGIN TOP NAVBAR */}
                        <Header />
                        {/* END TOP NAVBAR */}

                        {/* BEGIN CONTENT AREA */}
                        <Suspense>
                            <div className={`${themeConfig.animation} p-6 animate__animated`}>
                                {' '}
                                <Outlet />{' '}
                            </div>
                        </Suspense>
                        {/* END CONTENT AREA */}

                        {/* BEGIN FOOTER */}
                        <Footer />
                        {/* END FOOTER */}
                        <Portals />
                    </div>
                </div>

                {/* Quick Add Transaction Button */}
                <FloatingActionButton onClick={() => setIsQuickAddModalOpen(true)} labelKey="quick_add_transaction" />

                {/* Quick Add Transaction Modal */}
                <QuickAddTransactionModal isOpen={isQuickAddModalOpen} onClose={() => setIsQuickAddModalOpen(false)} />

                {/* Keyboard Shortcuts Help Modal */}
                <KeyboardShortcutsHelp isOpen={isShortcutsHelpOpen} onClose={() => setIsShortcutsHelpOpen(false)} shortcuts={shortcuts} />

                {/* AI Money Coach Widget */}
                <MoneyCoachWidget />
            </div>
        </App>
    );
};

export default DefaultLayout;
